import { forwardRef, useRef } from 'react'

import { Popover } from '@reach/popover'
import { styled } from 'styled-components'

import useOnClickOutside from 'peach/components/Modal/useClickOutside'
import { useToggle } from 'peach/hooks'

import KeyboardNavArea from '../$internal/KeyboardNavArea'
// `positionDefault, positionMatchWidth, positionRight`
import { positionMatchWidthMaxHeight } from '../Select/positions'

import MenuItem from './MenuItem'
import MenuTrigger from './MenuTrigger'
import { MenuConfigProvider } from './useMenuConfig'

const List = styled.div`
  margin-top: 2px;
  border: 1px solid ${(p) => p.theme.border};
  border-radius: 5px;
  box-shadow: 0 3px 4px rgb(0 0 0 / 20%);
  background-color: ${(p) => p.theme.offset};
  padding: 1px;
  min-width: 240px;
  min-height: 16px;
  overflow-y: auto;

  & > * {
    margin-top: 4px;
    margin-bottom: 4px;

    &:first-child {
      margin-top: 0;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }
`

const Content = styled.div`
  flex-grow: 1;
  padding: 4px;
  overflow-y: auto;
`

const Menu = forwardRef((props, ref) => {
  const {
    selectable,
    startOpen,
    children,
    render,
    label,
    searchControls,
    controls,
    disabled,
    primary,
    trigger,
    ...rest
  } = props

  const localRef = useRef()
  const $ref = ref ?? localRef

  const [isOpen, onOpen, onClose] = useToggle(startOpen)

  const contentRef = useRef()

  useOnClickOutside(onClose, contentRef, $ref)

  const renderProps = { onClose }

  const content = render ? render(renderProps) : children

  const isDisabled = disabled || _.isEmpty(content)

  const triggerProps = {
    ref: $ref,
    isOpen,
    onOpen,
    onClose,
    disabled: isDisabled,

    primary,
  }

  const displayTrigger = _.isFunction(trigger) ? (
    trigger(triggerProps)
  ) : trigger ? (
    <span>{trigger}</span>
  ) : (
    <MenuTrigger
      {...triggerProps}
      primary={primary}
      label={label || 'Actions'}
    />
  )

  return (
    <>
      {displayTrigger}

      {!isDisabled && isOpen && (
        <Popover
          ref={contentRef}
          targetRef={$ref}
          position={positionMatchWidthMaxHeight}
        >
          <KeyboardNavArea arrowFocus onEscape={onClose}>
            <List {...rest} ref={contentRef}>
              <MenuConfigProvider
                hasLeftSideSpace={selectable}
                onClose={onClose}
              >
                <Content>{content}</Content>
              </MenuConfigProvider>
            </List>
          </KeyboardNavArea>
        </Popover>
      )}
    </>
  )
})

Menu.displayName = 'Menu'

const MenuDivider = styled.div`
  margin-top: 8px;
  margin-bottom: 8px;
  border-bottom: 1px solid ${(p) => p.theme.border};
`
Menu.Divider = MenuDivider

Menu.Item = MenuItem

Menu.Link = MenuItem

export default Menu
